import Dialog from "../../../General/ConfirmableDialog";
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import {
  formatInChicago,
  parseDateUtcIfNoZone,
} from "../../../../utils/timezone";

interface HarshEventNotesDialogProps {
  open: boolean;
  onClose: () => void;
  harshEventId: string;
}

interface HarshEventNote {
  id?: string;
  note: string;
  date: string;
}

type HarshEvent = any;

const HarshEventNotesDialog: React.FC<HarshEventNotesDialogProps> = ({
  open,
  onClose,
  harshEventId,
}) => {
  const [newNote, setNewNote] = useState("");
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: harshEvent, isLoading } = useQuery<HarshEvent>({
    queryKey: ["harshEvent", harshEventId],
    queryFn: async () => {
      const response = await axios.get(
        `${apiVariable}/harshEvent/${harshEventId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data as HarshEvent;
    },
    enabled: !!harshEventId && !!token && open,
  });

  const addNoteMutation = useMutation({
    mutationFn: async (note: string) => {
      if (!harshEvent) throw new Error("Harsh event not loaded");
      const currentNotes: HarshEventNote[] = harshEvent.notes || [];
      const updatedNotes: HarshEventNote[] = [
        ...currentNotes,
        { note, date: new Date().toISOString() },
      ];

      const updatedHarshEvent = { ...harshEvent, notes: updatedNotes };

      const response = await axios.put(
        `${apiVariable}/harshEvent/${harshEventId}`,
        updatedHarshEvent,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data as HarshEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["harshEvent", harshEventId] });
      queryClient.invalidateQueries({ queryKey: ["harshEvents"] });
      setNewNote("");
    },
  });

  const handleAddNote = () => {
    if (newNote.trim()) {
      addNoteMutation.mutate(newNote);
    }
  };

  const notes: HarshEventNote[] = (harshEvent?.notes as HarshEventNote[]) || [];

  const sortedNotes = notes
    ? [...notes].sort(
        (a, b) =>
          parseDateUtcIfNoZone(b.date).getTime() -
          parseDateUtcIfNoZone(a.date).getTime()
      )
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Harsh Event Notes</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a new note..."
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim() || addNoteMutation.isPending}
            >
              Add Note
            </Button>
          </Box>

          {isLoading ? (
            <Typography>Loading notes...</Typography>
          ) : (
            <List>
              {sortedNotes.map((note, idx) => (
                <Paper
                  key={note.id || note.date || idx}
                  elevation={1}
                  sx={{ mb: 1, p: 1, backgroundColor: "background.default" }}
                >
                  <ListItem>
                    <ListItemText
                      primary={note.note}
                      secondary={formatInChicago(note.date)}
                    />
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HarshEventNotesDialog;
